import {
  IOrderFiltersInput,
  IOrderQueryResult,
  IPagination,
  ISortingDirection,
  isPcodeEnableForCurrentBrand,
  OrderStatus,
  SortingDirection,
  VehicleReserveStatus,
} from 'next-common';
import { useEffect, useState } from 'react';
import { IPcodeMaster } from 'sales-common';
import { useSearchParams, usePathname } from 'next/navigation';
import {
  findRouteForOrder,
  IRootUser,
  isStockOrder,
  Routes,
  selectedDealerId,
  useClientLazyQuery,
  useClientMutation,
  useClientQuery,
} from '../../../common';

import { getArticleCodeFromOrder } from '../../../utils';
import { CancelOrderModal } from '../../sales/sales-common/CancelOrderModal';
import { CancelReserveOrderModal } from '../../sales/sales-common/CancelReserveOrderModal';
import CreateOrder from './CreateOrder';
import { FilterBar } from './FilterBar';
import { OverviewHeader } from './Header';
import { IMyOrdersItem, MyOrders } from './MyOrders';
import { getStati, OrderCategory } from './orderQuery.utils';
import { LoadingSpinner } from '@repo/ui/components';
import { useUnifiedNavigate } from '@common/hooks/useUnifiedNavigation';
import { useAppSelector } from '@store/hooks';
import { selectEmployee } from '@store/selectors';
import { NEXT_ENTITY, NEXT_MUTATION_ENTITY } from '@common/interfaces/entity';

interface IMyOverviewProps extends IRootUser {}

interface IOrderQueryInput {
  filters: IOrderFiltersInput;
  pagination: IPagination;
  sorting: ISortingDirection;
}
const defaultSort: ISortingDirection = {
  fieldName: 'createdAt',
  direction: SortingDirection.DESC,
};
const defaultPagination: IPagination = {
  page: 1,
  itemsPerPage: 10,
  totalItems: 0,
};
export const defaultOrderFilter: IOrderFiltersInput = {
  dealerIds: [],
  salesPersonIdent: null,
  searchQuery: '',
  stati: getStati(OrderCategory.OFFER),
};

export const MyOverview = ({ isAdmin }: IMyOverviewProps) => {
  const { data: configuration } = useClientQuery(NEXT_ENTITY.CONFIGURATION);

  const [loading, setLoading] = useState<NodeJS.Timeout>(null);
  const [orders, setOrders] = useState<IOrderQueryResult[]>([]);
  const [orderId, setOrderId] = useState<string>(null);
  const [filters, setFilters] = useState<IOrderFiltersInput>(defaultOrderFilter);
  const [category, setCategory] = useState<OrderCategory>(OrderCategory.OFFER);
  const [pagination, setPagination] = useState<IPagination>(defaultPagination);
  const [pageChange, setPageChange] = useState(false);
  const [loadingForCancelReserve, setLoadingForCancelReserve] = useState<boolean>(false);
  const [reserveCancelModalStatus, setReserveCancelModalStatus] = useState<boolean>(false);
  const [pCodesLoading, setPCodesLoading] = useState<boolean>(false);
  const [showModal, setShowModal] = useState(false);
  const [reserveCancelOrderData, setReserveCancelOrderData] = useState(null);
  const [createOrder, setCreateOrder] = useState<boolean>(false);

  const sorting: ISortingDirection = defaultSort;

  const searchParams = useSearchParams();
  const salesPersonQuery = searchParams.get('selger');

  const router = useUnifiedNavigate();

  const [resumeOrderMutation] = useClientMutation(NEXT_MUTATION_ENTITY.RESUME_ORDER);
  const [reserveCancelVehicleOrderMutation] = useClientMutation(NEXT_MUTATION_ENTITY.RESERVE_VEIHICLE_ORDER);
  const [cancelOrderMutation] = useClientMutation(NEXT_MUTATION_ENTITY.CANCEL_ORDER);

  const pathname = usePathname();
  const employee = useAppSelector(selectEmployee);

  const setSalesPersonQuery = (value: string | null, replace: boolean) => {
    const params = new URLSearchParams(searchParams.toString());

    if (!value) {
      params.delete('selger');
    } else {
      params.set('selger', value);
    }
    router(`${pathname}?${params.toString()}`, { replace });
  };

  const queryOrdersInput = (): IOrderQueryInput => {
    return {
      filters,
      pagination: {
        ...pagination,
        page: pageChange ? pagination.page + 1 : 1,
      },
      sorting,
    };
  };

  const [getConfigBuildability] = useClientLazyQuery(NEXT_ENTITY.VEHICLE_CONFIGURATION_BUILDABILITY, {});

  const [getOrders] = useClientLazyQuery(NEXT_ENTITY.ALL_ORDERS, {
    onCompleted: (data) => {
      const newPage = (data && Array.isArray(data?.results) ? data.results : []) as unknown as IOrderQueryResult[];
      const isFirstPage = data?.pagination.page === 1;

      setOrders((prevOrders) => {
        // If first page, replace; otherwise, append
        return isFirstPage ? newPage : [...(prevOrders || []), ...newPage];
      });

      setPagination({
        ...data?.pagination,
        totalItems: data?.pagination.totalItems ?? 0,
      });

      setPageChange(null);
      setLoading(null);
    },
    onError: (error) => {
      // tslint:disable-next-line:no-console
      console.log(error);
      setOrders([]);
      setPageChange(null);
      setLoading(null);
    },
  });

  const [getPCodesList] = useClientLazyQuery(NEXT_ENTITY.PCODE_MASTER_LIST, {});

  const [vehicleByPimCode] = useClientLazyQuery(NEXT_ENTITY.VEHICLE_CONFIGURATION, {});

  const fetchOrders = () => {
    if (!filters.dealerIds || filters.dealerIds.length < 1) {
      setLoading(null);
      return;
    }

    if (loading) {
      clearTimeout(loading);
    }

    setLoading(
      setTimeout(() => {
        getOrders({
          variables: {
            input: queryOrdersInput(),
          },
        });
      }, 250)
    );
  };

  /**
   * Append the new page to the existing array instead of replacing it.
   */
  useEffect(() => {
    if (pageChange) {
      fetchOrders();
    }
  }, [pageChange]);

  /**
   * Refetch orders using the applied filter.
   */
  useEffect(() => {
    setOrders([]);
    fetchOrders();
  }, [filters]);

  useEffect(() => {
    filters.stati = getStati(category);
    setFilters({ ...filters });
  }, [category]);

  useEffect(() => {
    filters.salesPersonIdent = salesPersonQuery || null;
    filters.dealerIds = employee?.dealers?.map((dealer) => dealer?.dealerId) ?? [];
    setFilters({ ...filters });
  }, [employee, salesPersonQuery]);

  useEffect(() => {
    if (!employee) return;
    const currentDealerId = selectedDealerId();
    if (!currentDealerId) {
      const newDealerId = employee.departmentNumber;
      selectedDealerId(newDealerId);
    }
  }, [employee]);

  /**
   * Set default salesperson filter to the signed in user
   */
  useEffect(() => {
    if (employee?.employeeId && (salesPersonQuery === undefined || salesPersonQuery === null)) {
      setSalesPersonQuery(employee.employeeId, true);
    }
  }, [employee, salesPersonQuery, setSalesPersonQuery]);

  const resumeOrder = async (id: string) => {
    try {
      const data = await resumeOrderMutation({ variables: { orderId: id } });
      return data;
    } catch (error) {
      // tslint:disable-next-line:no-console
      console.error(error);
    }
  };

  const confirmDeleteOrder = async (confirmed: boolean) => {
    setShowModal(false);
    if (!confirmed) return;

    try {
      const data = await cancelOrderMutation({ variables: { orderId } });
      setOrders(orders.filter((order) => order.id !== data?.id));
    } catch (error) {
      // tslint:disable-next-line:no-console
      console.error(error);
    }
  };

  const handleSelect = (element: any, isElementClick: boolean = false) => {
    const route = Routes.getViewPage(element.id);
    if (isElementClick) {
      router(route);
    } else {
      window.open(route, '_blank');
    }
  };

  const findVehicleByPimCodeFromGjennoppta = async (element: any): Promise<IPcodeMaster[] | []> => {
    let vehicleList = [];
    if (element?.orderReference?.pimCode) {
      const vehicle = await vehicleByPimCode({ variables: { pimCode: element?.orderReference?.pimCode || '' } });
      if (vehicle) {
        vehicleList.push(vehicle);
      }
    }

    const articleCode = await getArticleCodeFromOrder(element, vehicleList);
    if (articleCode) {
      const PCodeList = await getPCodesList({ variables: { articleCode } });
      return PCodeList || [];
    }

    return [];
  };

  const handleContinue = async (element: IMyOrdersItem) => {
    const configId = element.externalPricing?.configId;
    if (configId) {
      getConfigBuildability({ variables: { configId } });
    }
    let route: string;
    const s = OrderStatus[element.status.toString()];
    const isVarebilEnabled = isPcodeEnableForCurrentBrand(configuration, null, element?.pimSnapshot?.make);
    if (s === OrderStatus.PAUSED || s === OrderStatus.OFFER_CANCELLED) {
      setOrderId(element.id);
      const order = await resumeOrder(element.id);
      if (!order) return;
      route = findRouteForOrder(order);
    } else if (!isStockOrder(element?.lead?.orderType) && isVarebilEnabled) {
      setPCodesLoading(true);
      const pCodeList = await findVehicleByPimCodeFromGjennoppta(element);
      setPCodesLoading(false);
      if (pCodeList?.length) {
        route = Routes.getImporterOptionsPage(element?.id);
      } else {
        route = Routes.getVehicleAccessoriesPage(element?.id);
      }
    } else {
      route = findRouteForOrder(element);
    }
    setTimeout(() => {
      router(route);
    }, 100);
  };

  const updateFilter = (field, value) => {
    if (filters?.[field] === value) {
      return;
    }
    filters[field] = value;
    setFilters({ ...filters });
  };

  const updateCategory = (value) => {
    setCategory(value);
  };

  const handleLoadMore = () => {
    setPageChange(true);
  };

  const handleCancelReservation = async (element: any) => {
    setReserveCancelModalStatus(true);
    setReserveCancelOrderData({
      id: element?.id,
      customerId: element?.customer?.bosCustomer.id,
    });
  };

  const handleDelete = async (element: any) => {
    setOrderId(element.id);
    setShowModal(true);
  };

  const confirmReserveCancelClick = async () => {
    setReserveCancelModalStatus(false);
    setLoadingForCancelReserve(true);
    try {
      await reserveCancelVehicleOrderMutation({
        variables: {
          id: reserveCancelOrderData?.id,
          status: VehicleReserveStatus[VehicleReserveStatus.WAITING_CANCELLATION],
          customerId: reserveCancelOrderData?.customerId,
        },
      });
    } catch (error) {
      // tslint:disable-next-line:no-console
      console.error(error);
    }
    fetchOrders();
    setLoadingForCancelReserve(false);
  };

  return (
    <>
      <OverviewHeader
        isAdmin={isAdmin}
        onClick={() => setCreateOrder(true)}
        employee={employee}
        filter={filters}
        category={category}
        onChange={updateFilter}
        onCategoryChange={updateCategory}
      />
      {createOrder && (
        <CreateOrder onClose={() => setCreateOrder(false)} createOrder={createOrder} configuration={configuration} />
      )}
      <div className="grid grid-cols-[18rem_auto] gap-6 max-lg:grid-cols-1 max-lg:gap-4">
        <FilterBar onChange={updateFilter} onCategoryChange={updateCategory} configuration={configuration} />
        <MyOrders
          category={category}
          pagination={pagination}
          onLoadMore={handleLoadMore}
          onSelect={handleSelect}
          onContinue={handleContinue}
          onCancelReservation={handleCancelReservation}
          onDelete={handleDelete}
          data={orders}
          loading={!!loading}
        />
      </div>
      {reserveCancelModalStatus && (
        <CancelReserveOrderModal
          modalStatus={reserveCancelModalStatus}
          closeModalClick={() => setReserveCancelModalStatus(false)}
          confirmReserveCancelClick={confirmReserveCancelClick}
        />
      )}
      <CancelOrderModal show={showModal} isOrder={false} onConfirm={confirmDeleteOrder} />
      {(loadingForCancelReserve || pCodesLoading) && <LoadingSpinner isModal={true} />}
    </>
  );
};

export default MyOverview;
